import * as React from 'react';

import { createClient } from '@rematter/frontend-lib-api-client';
import {
  QueryClient,
  QueryClientProvider,
  keepPreviousData,
} from '@tanstack/react-query';
import { createClient as createSSEClient } from 'graphql-sse';
import { useHydrateAtoms } from 'jotai/utils';
import { queryClientAtom } from 'jotai-tanstack-query';

import { ApiContext } from './ApiContext';

import { config } from '~/config';
import { useIdentity } from '~/shared/hooks';

// Component to hydrate the queryClientAtom with the shared QueryClient
const HydrateAtoms = ({
  queryClient,
  children,
}: React.PropsWithChildren<{ queryClient: QueryClient }>) => {
  useHydrateAtoms(new Map([[queryClientAtom, queryClient]]));
  return children;
};

export const ApiProvider = ({ children }: React.PropsWithChildren) => {
  const { accessToken } = useIdentity();

  const accessTokenRef = React.useRef(accessToken);
  React.useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const graphqlUrl = `${config.api.url}/graphql`;
  const graphqlSSEUrl = `${config.api.url}/graphql`;

  const graphqlClient = React.useCallback(
    (queryName: string) =>
      createClient({
        url: `${graphqlUrl}?q=${queryName}`,
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        credentials: 'include',
      }),
    [graphqlUrl, accessToken],
  );

  const graphqlSSEClient = React.useCallback(
    (queryName: string) =>
      createSSEClient({
        url: `${graphqlSSEUrl}?q=${queryName}`,
        headers: () => ({
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(accessTokenRef.current
            ? { Authorization: `Bearer ${accessTokenRef.current}` }
            : {}),
        }),
        singleConnection: false,
        credentials: 'include',
      }),
    [graphqlSSEUrl],
  );

  const reactQueryClient = React.useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            placeholderData: keepPreviousData,
          },
        },
      }),
    [],
  );

  const value = React.useMemo(
    () => ({
      client: (queryName: string) => graphqlClient(queryName),
      sseClient: (queryName: string) => graphqlSSEClient(queryName),
    }),
    [graphqlClient, graphqlSSEClient],
  );

  return (
    <QueryClientProvider client={reactQueryClient}>
      <HydrateAtoms queryClient={reactQueryClient}>
        <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
      </HydrateAtoms>
    </QueryClientProvider>
  );
};
